import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef } from "react";
import { Blog, ContentPageTheme } from "@/services/bedrock";
import { EditableBlog } from "./BlogEditor";
import { BlogPreviewAuthor, toBlogPreviewPayload } from "./blogPreviewPayload";
import { LivePreviewFrame, useLivePreviewIframe } from "./LivePreviewFrame";

export type BlogPreviewPart = "title" | "subtitle" | "excerpt" | "tags" | "takeaways";

export type BlogPreviewHandle = {
  highlightSection: (index: number | null, blockIndex?: number) => void;
  hoverPart: (part: BlogPreviewPart | null) => void;
  clearFocusedBlock: () => void;
  scrollToSection: (index: number, blockIndex?: number) => void;
  followSection: (index: number) => void;
  unfollowSection: () => void;
  scrollToPart: (part: BlogPreviewPart) => void;
  highlightPart: (part: BlogPreviewPart | null) => void;
};

interface BlogPreviewProps {
  blog: Blog;
  draft: EditableBlog;
  theme?: ContentPageTheme | null;
  author?: BlogPreviewAuthor;
  shareUrl?: string;
  onCopyShareUrl?: () => void;
  onOpenPage?: () => void;
  className?: string;
}

export const BlogPreview = forwardRef<BlogPreviewHandle, BlogPreviewProps>(
  ({ blog, draft, theme, author, shareUrl, onCopyShareUrl, onOpenPage, className }, ref) => {
    const followIndexRef = useRef<number | null>(null);
    const unhoverTimerRef = useRef<number | null>(null);
    const { iframeRef, iframeOrigin, postToPreview, onIframeLoad } = useLivePreviewIframe();
    const payload = useMemo(() => toBlogPreviewPayload(blog, draft, theme, author), [blog, draft, theme, author]);

    const cancelUnhover = useCallback(() => {
      if (unhoverTimerRef.current == null) return;
      window.clearTimeout(unhoverTimerRef.current);
      unhoverTimerRef.current = null;
    }, []);

    const sendHover = useCallback(
      (message: Record<string, unknown>) => {
        cancelUnhover();
        postToPreview(message);
      },
      [cancelUnhover, postToPreview]
    );

    const sendUnhover = useCallback(() => {
      cancelUnhover();
      unhoverTimerRef.current = window.setTimeout(() => {
        unhoverTimerRef.current = null;
        postToPreview({ type: "BLOG_SECTION", action: "unhover" });
      }, 60);
    }, [cancelUnhover, postToPreview]);

    useEffect(() => () => cancelUnhover(), [cancelUnhover]);

    const sendPayload = useCallback(() => {
      postToPreview({ type: "BLOG", payload, followSectionIndex: followIndexRef.current });
    }, [postToPreview, payload]);

    useImperativeHandle(
      ref,
      () => ({
        highlightSection: (index: number | null, blockIndex?: number) => {
          if (index == null) {
            sendUnhover();
            return;
          }
          sendHover({ type: "BLOG_SECTION", action: "hover", sectionIndex: index, blockIndex });
        },
        hoverPart: (part: BlogPreviewPart | null) => {
          if (part == null) {
            sendUnhover();
            return;
          }
          sendHover({ type: "BLOG_SECTION", action: "hover", part });
        },
        clearFocusedBlock: () => {
          postToPreview({ type: "BLOG_SECTION", action: "clearFocus" });
        },
        scrollToSection: (index: number, blockIndex?: number) => {
          postToPreview({ type: "BLOG_SECTION", action: "scroll", sectionIndex: index, blockIndex });
        },
        followSection: (index: number) => {
          followIndexRef.current = index;
          postToPreview({ type: "BLOG_SECTION", action: "follow", sectionIndex: index });
        },
        unfollowSection: () => {
          followIndexRef.current = null;
          postToPreview({ type: "BLOG_SECTION", action: "unfollow" });
        },
        scrollToPart: (part: BlogPreviewPart) => {
          followIndexRef.current = null;
          postToPreview({ type: "BLOG_SECTION", action: "scroll", part });
        },
        highlightPart: (part: BlogPreviewPart | null) => {
          postToPreview({ type: "BLOG_SECTION", action: part == null ? "clearFocus" : "highlight", part });
        },
      }),
      [postToPreview, sendHover, sendUnhover]
    );

    useEffect(() => {
      sendPayload();
    }, [sendPayload]);

    return (
      <LivePreviewFrame
        iframeRef={iframeRef}
        iframeOrigin={iframeOrigin}
        srcPath="/b/preview"
        title="Blog preview"
        openPageAriaLabel="Open blog page"
        shareUrl={shareUrl}
        onCopyShareUrl={onCopyShareUrl}
        onOpenPage={onOpenPage}
        onIframeLoad={onIframeLoad}
        className={className}
      />
    );
  }
);

BlogPreview.displayName = "BlogPreview";
